import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { confirmPasswordReset } from '../api/auth.js'
import { firstError } from '../utils/format.js'
import { BrandLockup } from '../components/Brand.jsx'

/**
 * Reached from the email link: /reset/<uid>/<token>.
 *
 * Lets the user pick a new password. On success → /login with a flash
 * banner so the user knows their password was changed.
 */
export default function ResetPassword() {
  const { uid = '', token = '' } = useParams()
  const nav = useNavigate()
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [ok, setOk] = useState(false)

  const strength = useMemo(() => passwordStrength(pw), [pw])

  async function onSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return }
    if (pw !== confirm) { setErr('Passwords do not match.'); return }

    setBusy(true)
    try {
      await confirmPasswordReset({ uid, token, new_password: pw })
      setOk(true)
      // Brief delay so the user sees the success state before redirect.
      setTimeout(() => nav('/login', { replace: true }), 1500)
    } catch (e) {
      setErr(firstError(e, 'Reset link is invalid or expired.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-wantok-50 via-cream to-coral-50/50 flex flex-col">
      <header className="bg-wantok-gradient text-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <BrandLockup variant="light" linkTo="/login" size={36} />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">Account recovery</div>
          <h1 className="text-2xl font-display font-bold text-slate-900 mt-1">Choose a new password</h1>

          {ok ? (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
              <div className="font-semibold">Password updated</div>
              <p className="mt-1 text-emerald-700">Redirecting to sign in…</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Pick a strong password. At least 8 characters, ideally a mix of upper/lower/numbers/symbols.
              </p>

              <label className="block text-xs text-slate-700 font-semibold">
                New password
                <input
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  autoFocus required minLength={8} autoComplete="new-password"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400 font-normal"
                />
                {pw && <StrengthBar score={strength.score} label={strength.label} />}
              </label>

              <label className="block text-xs text-slate-700 font-semibold">
                Confirm new password
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required minLength={8} autoComplete="new-password"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400 font-normal"
                />
              </label>

              {err && (
                <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
                  {err}
                </div>
              )}

              <button type="submit" disabled={busy}
                      className="w-full bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60">
                {busy ? 'Updating password…' : 'Update password'}
              </button>

              <div className="text-center text-xs text-slate-500 pt-2 border-t border-slate-100">
                Link expired or not working?{' '}
                <Link to="/forgot" className="text-wantok-700 underline font-medium">Request a new link</Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  )
}

function StrengthBar({ score, label }) {
  const widths = ['10%', '25%', '50%', '75%', '100%']
  const colors = ['bg-rose-400', 'bg-rose-300', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-600']
  return (
    <div className="mt-1">
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-300 ${colors[score]}`}
             style={{ width: widths[score] }} />
      </div>
      <div className="text-[11px] text-slate-500 mt-1 font-normal">{label}</div>
    </div>
  )
}

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '' }
  let score = 0
  if (pw.length >= 8)  score++
  if (pw.length >= 12) score++
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++
  if (/\d/.test(pw))   score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  score = Math.min(score, 4)
  const labels = ['Too short', 'Weak', 'Fair', 'Strong', 'Very strong']
  return { score, label: labels[score] }
}
